import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';

const CANDIDATES_DATABASE_ROLE = {
  NO_ACCESS: 0,
  BASIC: 1,
  FULL_ACCESS: 2
};

interface CompanyTrialModalProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
  onSave: () => void;
}

const CompanyTrialModal: React.FC<CompanyTrialModalProps> = ({ companyId, companyName, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    company_id: companyId,
    trial: true,
    active: true,
    trial_ends_at: '',
    trial_date: '',
    trial_time: '',
    candidates_database_role: CANDIDATES_DATABASE_ROLE.FULL_ACCESS,
    candidates_database_alerts_per_month: 300,
    candidates_database_contacts: 5,
    caesar_slots: 0,
    opportunities_slots: 1,
    recruiters_slots: 0,
    highlight_slots: -1,
    confidentials: -1,
    deal_id: '',
    opportunities_included: true // Campo que estava faltando
  });
  const [submitting, setSubmitting] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);

  useEffect(() => {
    // Set default trial end date (2 days from now, +1 hour)
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 2);
    defaultEndDate.setHours(defaultEndDate.getHours() + 1);
    
    const dateStr = defaultEndDate.toLocaleDateString('pt-BR').split('/').reverse().join('-');
    const timeStr = defaultEndDate.toTimeString().slice(0, 5);
    
    // Ajustar para compensar fuso horário
    const adjustedDate = new Date(defaultEndDate);
    adjustedDate.setHours(adjustedDate.getHours() + 3);
    
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    const hours = String(adjustedDate.getHours()).padStart(2, '0');
    const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
    
    setFormData(prev => ({
      ...prev,
      trial_date: dateStr,
      trial_time: timeStr,
      trial_ends_at: `${year}-${month}-${day} ${hours}:${minutes}:00`
    }));
  }, []);

  useEffect(() => {
    calculateDaysLeft();
  }, [formData.trial_ends_at]);

  const calculateDaysLeft = () => {
    if (formData.trial_ends_at) {
      const diff = Math.floor((Date.parse(formData.trial_ends_at) - Date.now()) / (1000 * 3600 * 24));
      setDaysLeft(diff > 0 ? diff : 0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.trial_ends_at) {
      toast.error('Data de término do trial é obrigatória');
      return;
    }

    try {
      setSubmitting(true);
      
      // Debug: mostrar a data que está sendo enviada
      console.log('Data sendo enviada:', formData.trial_ends_at);
      console.log('Dados completos:', formData);
      
      await api.post(`/companies/${companyId}/subscriptions`, formData);
      toast.success('Trial criado com sucesso!');
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao criar trial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateTimeChange = (field: 'date' | 'time', value: string) => {
    const newFormData = { ...formData };
    
    if (field === 'date') {
      newFormData.trial_date = value;
    } else {
      newFormData.trial_time = value;
    }
    
    // Combinar data e hora para trial_ends_at no fuso horário local
    if (newFormData.trial_date && newFormData.trial_time) {
      // Criar data no fuso horário local (sem conversão UTC)
      const localDate = new Date(`${newFormData.trial_date}T${newFormData.trial_time}:00`);
      // Ajustar para compensar o fuso horário (adicionar 3 horas para UTC-3)
      localDate.setHours(localDate.getHours() + 3);
      
      // Formatar como string local
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const hours = String(localDate.getHours()).padStart(2, '0');
      const minutes = String(localDate.getMinutes()).padStart(2, '0');
      
      newFormData.trial_ends_at = `${year}-${month}-${day} ${hours}:${minutes}:00`;
    }
    
    setFormData(newFormData);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Novo Trial - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.content}>
            <div style={styles.leftColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Configurações Gerais</h3>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Banco de Talentos:</label>
                  <select
                    value={formData.candidates_database_role}
                    onChange={(e) => handleInputChange('candidates_database_role', Number(e.target.value))}
                    style={styles.select}
                  >
                    <option value={CANDIDATES_DATABASE_ROLE.FULL_ACCESS}>ACESSO COMPLETO</option>
                    <option value={CANDIDATES_DATABASE_ROLE.BASIC}>ACESSO BÁSICO</option>
                    <option value={CANDIDATES_DATABASE_ROLE.NO_ACCESS}>SEM ACESSO</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Data de término do trial:</label>
                  <input
                    type="date"
                    value={formData.trial_date}
                    onChange={(e) => handleDateTimeChange('date', e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Horário:</label>
                  <input
                    type="time"
                    value={formData.trial_time}
                    onChange={(e) => handleDateTimeChange('time', e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Dias de trial:</label>
                  <div style={styles.daysLeft}>{daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}</div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Pipedrive Deal ID:</label>
                  <input
                    type="text"
                    value={formData.deal_id}
                    onChange={(e) => handleInputChange('deal_id', e.target.value)}
                    style={styles.input}
                    placeholder="ID do deal no Pipedrive"
                  />
                </div>
              </div>
            </div>

            <div style={styles.rightColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Banco de Talentos</h3>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Total de alertas:</label>
                  <input
                    type="number"
                    value={formData.candidates_database_alerts_per_month}
                    onChange={(e) => handleInputChange('candidates_database_alerts_per_month', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Qtd de contatos abertos:</label>
                  <input
                    type="number"
                    value={formData.candidates_database_contacts}
                    onChange={(e) => handleInputChange('candidates_database_contacts', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Caesar</h3>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>SLOTS:</label>
                  <input
                    type="number"
                    value={formData.caesar_slots}
                    onChange={(e) => handleInputChange('caesar_slots', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Vagas</h3>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Slots de Vagas:</label>
                  <input
                    type="number"
                    value={formData.opportunities_slots}
                    onChange={(e) => handleInputChange('opportunities_slots', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.opportunities_included}
                      onChange={(e) => {
                        const included = e.target.checked;
                        handleInputChange('opportunities_included', included);
                        // Ajustar opportunities_slots baseado no checkbox
                        if (included && formData.opportunities_slots < 0) {
                          handleInputChange('opportunities_slots', 1);
                        } else if (!included && formData.opportunities_slots > -1) {
                          handleInputChange('opportunities_slots', -1);
                        }
                      }}
                      style={styles.checkbox}
                    />
                    {formData.opportunities_included 
                      ? 'Oportunidades inclusas no plano (sem checkout)' 
                      : 'Não incluso no plano (checkout na hora da publicação)'
                    }
                  </label>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Slots de Recrutadores:</label>
                  <input
                    type="number"
                    value={formData.recruiters_slots}
                    onChange={(e) => handleInputChange('recruiters_slots', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Slots de destaques:</label>
                  <input
                    type="number"
                    value={formData.highlight_slots}
                    onChange={(e) => handleInputChange('highlight_slots', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Qtd. Confidenciais:</label>
                  <input
                    type="number"
                    value={formData.confidentials}
                    onChange={(e) => handleInputChange('confidentials', Number(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.submitBtn,
                ...(submitting ? { backgroundColor: colors.textLight, cursor: 'not-allowed' } : {})
              }}
            >
              {submitting ? 'Criando...' : 'Criar Trial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(90vh - 120px)',
  },
  content: {
    display: 'flex',
    flex: 1,
    gap: '2rem',
    padding: '1.5rem',
    overflow: 'auto',
  },
  leftColumn: {
    flex: '1 1 60%',
  },
  rightColumn: {
    flex: '1 1 40%',
    minWidth: '350px',
  },
  section: {
    marginBottom: '2rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '1rem',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    color: colors.primary,
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  numberInput: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1.2rem',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  daysLeft: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: colors.primary,
    padding: '0.5rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    padding: '1.5rem',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.textLight,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  submitBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    color: colors.text,
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
};

export default CompanyTrialModal;
